import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BbLevel,
  BbLevelProbabilityCell,
  bbGetLevelProbabilities,
  bbGetLevels,
  bbSetLevelProbability,
} from '../../../services/blindBoxService';

export function SectionLevelMatrix({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [cells, setCells] = useState<BbLevelProbabilityCell[]>([]);
  const [levels, setLevels] = useState<BbLevel[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const toPercent = (raw?: string) => {
    const n = Number(raw ?? 0);
    if (!Number.isFinite(n)) return '0.0';
    return (n * 100).toFixed(1);
  };

  const percentToRaw = (pct: string) => {
    const n = Number(pct);
    if (!Number.isFinite(n)) return null;
    return (n / 100).toString();
  };

  useEffect(() => {
    Promise.all([bbGetLevelProbabilities(), bbGetLevels()]).then(([c, l]) => {
      setCells(c);
      setLevels(l.sort((a, b) => a.displayOrder - b.displayOrder));
    });
  }, [refreshKey]);

  /** 矩阵只展示启用中的奖励（与后端 is_active / isActive 一致） */
  const visibleCells = useMemo(
    () => cells.filter((c) => c.isActive === true),
    [cells],
  );

  useEffect(() => {
    // cells 更新后，同步一份可编辑的 % 值（key: rewardId-levelId）
    const next: Record<string, string> = {};
    visibleCells.forEach((c) => {
      next[`${c.rewardId}-${c.levelId}`] = toPercent(c.probability);
    });
    setDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCells]);

  const byReward = useMemo(() => {
    const m = new Map<number, BbLevelProbabilityCell[]>();
    visibleCells.forEach((c) => {
      if (c.sourceModule !== 'BOX') return;
      const arr = m.get(c.rewardId) || [];
      arr.push(c);
      m.set(c.rewardId, arr);
    });
    return m;
  }, [visibleCells]);

  const updateCell = async (rewardId: number, levelId: number, percent: string) => {
    const raw = percentToRaw(percent);
    if (raw === null) {
      notify(t('module.blindBox.invalidNumber', { _: 'Invalid number' }), { type: 'warning' });
      return;
    }
    try {
      await bbSetLevelProbability({ rewardId, levelId, probability: raw });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      bbGetLevelProbabilities().then(setCells);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const rewardIds = Array.from(byReward.keys());
  const columnTotals = useMemo(() => {
    const rewardRows = Array.from(byReward.values());
    return levels.map((lv) => {
      let sum = 0;
      rewardRows.forEach((row) => {
        const cell = row.find((c) => c.levelId === lv.id);
        const n = parseFloat(cell?.probability || '0');
        if (!Number.isNaN(n)) sum += n;
      });
      return sum;
    });
  }, [byReward, levels]);

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>reward</TableCell>
            {levels.map((lv) => (
              <TableCell key={lv.id}>
                Lv.{lv.displayOrder} {lv.name} (%)
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rewardIds.map((rid) => {
            const row = byReward.get(rid) || [];
            const name = row[0]?.rewardName || rid;
            return (
              <TableRow key={rid}>
                <TableCell>{name}</TableCell>
                {levels.map((lv) => {
                  const cell = row.find((c) => c.levelId === lv.id);
                  const k = `${rid}-${lv.id}`;
                  return (
                    <TableCell key={lv.id}>
                      <TextField
                        size="small"
                        value={draft[k] ?? toPercent(cell?.probability)}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            [k]: e.target.value,
                          }))
                        }
                        onBlur={(e) => updateCell(rid, lv.id, e.target.value)}
                        sx={{ width: 72 }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          <TableRow
            sx={(theme) => ({
              '& td': {
                backgroundColor:
                  theme.vars?.palette?.background?.neutral ?? theme.palette.action.hover,
                fontWeight: theme.typography.fontWeightSemiBold,
              },
            })}
          >
            <TableCell>{t('module.blindBox.columnTotal')}</TableCell>
            {levels.map((lv, idx) => (
              <TableCell key={lv.id}>{`${((columnTotals[idx] ?? 0) * 100).toFixed(1)}`}</TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
